import { Alert, CircularProgress } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useBoolean } from 'usehooks-ts';

import { getLocalDocumentUrlFromS3 } from '@/api/document';

import { Icon } from '@/components/Icon/Icon';
import { ConfirmDrawer, Drawer, DrawerProps } from '@/components/Layout/Drawer';

import { useDrawerOpenTracker } from '@/context';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectOperationUser } from '@/redux/operation/selectors';

import {
  Document,
  DocumentStatus,
  DocumentType,
  RequiredDocumentSection,
  SourceType,
} from '@/types/document';
import { UserStatus } from '@/types/user';

import { getAPIErrorMessage } from '@/utils/api';
import { isImageContentType, isPDFContentType } from '@/utils/string';
import { isCaymanUser } from '@/utils/user';

import { DocumentPreview } from '../components/DocumentPreview';
import { RequiredDocumentUploadDrawer } from './RequiredDocumentUploadDrawer';

import { DrawerContainer, DrawerLoader } from './Drawers.styled';

type ViewDocumentDrawerProps = {
  section: RequiredDocumentSection;
  document?: Document;
  clientId?: string | number;
  onReUploaded?: () => void;
  downloadPath?: string;
  downloadFileName?: string;
} & DrawerProps;

export function ViewDocumentDrawer({
  section,
  document,
  clientId,
  title,
  onClose,
  onReUploaded,
  downloadPath,
  downloadFileName,
  ...props
}: ViewDocumentDrawerProps) {
  const { t } = useTranslate();

  const loggedInUser = useAppSelector(selectLoggedInUser);
  const operationUser = useAppSelector(selectOperationUser);
  const user = clientId ? operationUser : loggedInUser;
  const {
    value: isUploading,
    setTrue: showUploader,
    setFalse: hideUploader,
  } = useBoolean(false);
  const {
    value: isReupConfirming,
    setTrue: showReupConfirm,
    setFalse: hideReupConfirm,
  } = useBoolean(false);

  useDrawerOpenTracker(isUploading || isReupConfirming);

  const {
    data: fileUrl,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['view-document', document],
    queryFn: () => getLocalDocumentUrlFromS3(document!.uri as string),
    enabled: !!document?.uri,
  });

  const srcType = isPDFContentType(document?.contentType)
    ? SourceType.PDF
    : isImageContentType(document?.contentType)
      ? SourceType.Image
      : undefined;

  const onShowConfirm =
    user?.status === UserStatus.Approved ||
    document?.status === DocumentStatus.Approved
      ? undefined
      : () => {
          if (document?.status === DocumentStatus.Rejected) {
            showUploader?.();
          } else {
            showReupConfirm();
          }
        };

  const handleReup = () => {
    hideReupConfirm();
    showUploader?.();
  };

  const handleUploadFinished = () => {
    if (onReUploaded) {
      onReUploaded();
    }
    onClose();
    hideUploader();
  };

  const isReuploadDisabled =
    !isCaymanUser(user) && !!user?.infoAccuracyConfirmed;

  return (
    <Drawer
      {...props}
      onClose={onClose}
      title={title}
      bgColor={(theme: Theme) => theme.palette.background.paper}
      headerBgColor={(theme: Theme) => theme.palette.background.paper}
    >
      <DrawerContainer>
        {fileUrl ? (
          <DocumentPreview
            title={t(`documentType.${document?.docType as DocumentType}`)}
            src={fileUrl}
            srcType={srcType}
            onCancel={onClose}
            onConfirm={onShowConfirm}
            confirmText={t('common.reupload')}
            isConfirmDisabled={isReuploadDisabled}
          />
        ) : isLoading ? (
          <DrawerLoader>
            <CircularProgress />
          </DrawerLoader>
        ) : error ? (
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {getAPIErrorMessage(error)}
          </Alert>
        ) : null}
      </DrawerContainer>
      {isReupConfirming && (
        <ConfirmDrawer
          title={t('question.reupload')}
          description={t('question.reuploadDetails')}
          primaryAction={handleReup}
          primaryText={t('common.confirm')}
          secondaryAction={hideReupConfirm}
          secondaryText={t('common.cancel')}
          onClose={hideReupConfirm}
        />
      )}
      {document && (
        <RequiredDocumentUploadDrawer
          open={isUploading}
          section={document.type}
          type={document.docType as DocumentType}
          clientId={clientId}
          onClose={hideUploader}
          onUploaded={handleUploadFinished}
          title={t(section.displayText || section.type)}
          downloadPath={downloadPath}
          downloadFileName={downloadFileName}
        />
      )}
    </Drawer>
  );
}
